/**
 * Zod schema for the public job page (`/jobs/:jobId`,
 * `apps/web/src/app/routes/PublicJobPage.tsx`) — GitHub issue #301,
 * "[GEO] JobPosting schema markup on public job pages — free Google for
 * Jobs indexing".
 *
 * Mirrors `apps/workers/src/api/publicJobs.ts`'s `PublicJobDTO` (`GET
 * /api/public/jobs/:jobId`). `status` is intentionally narrowed to
 * `ACTIVE | PAUSED` — that endpoint never returns a body for DRAFT
 * (404'd) or CLOSED/archived (410'd) jobs, so a response that parses
 * against this schema is, by construction, a live/public job.
 */

import { z } from 'zod';

export const publicJobSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  location: z.string().nullable(),
  isRemote: z.boolean(),
  /** ISO 8601 — the job's `datePosted` for JobPosting JSON-LD. */
  datePosted: z.string(),
  /** ISO 8601, always a real future date on a live job (see
   * `@herohire/types`'s `computeValidThrough`). */
  validThrough: z.string(),
  /** The CUSTOMER company (never "Hero"/"Hero Hire AI"). */
  hiringOrganizationName: z.string(),
  employmentType: z.string(),
  status: z.enum(['ACTIVE', 'PAUSED']),
  jobPageUrl: z.string(),
});

export type PublicJobDTO = z.infer<typeof publicJobSchema>;
